import CantileverButton from 'components/CantileverButton'
import CantileverRow from 'components/CantileverRow'
import { TextInputStyled } from 'components/form/inputs/InputStyles'
import { forecastMath } from 'features/math'
import { getSensorName, useScatterplotContext } from 'features/timeSeries'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import {
  SectionContent,
  SensorControls,
  SmallText,
  SpacedSection,
} from 'features/timeSeries/sharedStyles'
import { Minus, Pencil } from 'lucide-react'
import React, { useCallback, useState } from 'react'
import { GeckoTheme } from 'styles/GeckoTheme'
import {
  BaseSensorEntry,
  NEW_DERIVED_VALUE_ENTRY,
  TimeSeriesDerivedValueEntryJSON,
} from 'types'

import { FormulaBuilder } from './FormulaBuilder'

interface DerivedValuesProps {
  sensorEntries: BaseSensorEntry[]
  style?: React.CSSProperties
}

export const DerivedValues: React.FC<DerivedValuesProps> = ({
  sensorEntries,
  style,
}) => {
  const { parse } = forecastMath
  const {
    derivedValues,
    createDerivedValues,
    setDerivedValues,
    handleRemoveDerivedValue,
    selectedSensors,
  } = useScatterplotContext()

  // State for creating new derived value
  const [editingDerivedId, setEditingDerivedId] = useState<string | null>(null)

  const [newDerived, setNewDerived] = useState<TimeSeriesDerivedValueEntryJSON>(
    NEW_DERIVED_VALUE_ENTRY
  )
  const [expressionError, setExpressionError] = useState<string | null>(null)

  const hasSelectedSensors = selectedSensors.length > 0

  const handleAddDerivedValue = useCallback(async () => {
    try {
      const result = await createDerivedValues(newDerived, editingDerivedId)
      setDerivedValues(result)
    } catch (error) {
      setExpressionError(
        error instanceof Error ? error.message : 'An unknown error occurred.'
      )
      return
    }

    setNewDerived(NEW_DERIVED_VALUE_ENTRY)
    setExpressionError(null)
    setEditingDerivedId(null)
  }, [setDerivedValues, createDerivedValues, newDerived, editingDerivedId])

  const handleFormulaChange = useCallback((expression: string) => {
    setNewDerived((newDerived) => {
      return {
        ...newDerived,
        data: {
          ...newDerived.data,
          expression,
        },
      }
    })
    setExpressionError(null)
  }, [])

  return (
    <SectionContent style={style}>
      <SmallText type='secondary'>
        Create calculated values from sensor combinations
      </SmallText>

      {derivedValues.length > 0 && (
        <SensorControls>
          {derivedValues.map((dv) => (
            <CantileverRow
              key={dv.id}
              leadingContent={
                <ChartTooltip
                  label={
                    <div>
                      <div>
                        {(() => {
                          try {
                            const expr = parse(dv.data.expression).toString()
                            return dv.data.unit
                              ? `${expr} (${dv.data.unit})`
                              : expr
                          } catch {
                            return dv.data.unit
                              ? `${dv.data.expression} (${dv.data.unit})`
                              : dv.data.expression
                          }
                        })()}
                      </div>
                      {dv.data.description && <div>{dv.data.description}</div>}
                    </div>
                  }>
                  <span>{dv.data.name}</span>
                </ChartTooltip>
              }
              trailingContent={
                <CantileverRow>
                  <CantileverButton
                    iconOnly
                    onClick={() => {
                      setEditingDerivedId(dv.id)
                      setNewDerived(dv)
                      setExpressionError(null)
                    }}>
                    <Pencil size={14} />
                  </CantileverButton>
                  <CantileverButton
                    danger
                    iconOnly
                    onClick={async () => await handleRemoveDerivedValue(dv.id)}>
                    <Minus size={14} />
                  </CantileverButton>
                </CantileverRow>
              }
            />
          ))}
        </SensorControls>
      )}

      <SpacedSection>
        <TextInputStyled
          type={'input'}
          $borderColor={GeckoTheme.forecast.border2}
          mode={'edit'}
          placeholder='Name (e.g., Power Output)'
          value={newDerived.data.name}
          onChange={(e) =>
            setNewDerived({
              ...newDerived,
              data: { ...newDerived.data, name: e.target.value },
            })
          }
          disabled={!hasSelectedSensors}
        />
        <TextInputStyled
          placeholder='Unit (e.g., kW, m/s)'
          $borderColor={GeckoTheme.forecast.border2}
          value={newDerived.data.unit || ''}
          onChange={(e) =>
            setNewDerived({
              ...newDerived,
              data: { ...newDerived.data, unit: e.target.value },
            })
          }
          disabled={!hasSelectedSensors}
        />
        <TextInputStyled
          $borderColor={GeckoTheme.forecast.border2}
          placeholder='Description (optional)'
          value={newDerived.data.description || ''}
          onChange={(e) =>
            setNewDerived({
              ...newDerived,
              data: { ...newDerived.data, description: e.target.value },
            })
          }
          disabled={!hasSelectedSensors}
        />
        <FormulaBuilder
          sensors={[
            ...sensorEntries.map((s) => ({
              id: s.id,
              name: getSensorName(s.id, derivedValues, sensorEntries),
            })),
            ...derivedValues
              .filter((dv) => dv.id !== editingDerivedId)
              .map((dv) => ({
                id: dv.id,
                name: dv.data.name,
              })),
          ]}
          value={newDerived.data.expression || ''}
          onChange={handleFormulaChange}
          onError={setExpressionError}
          disabled={!hasSelectedSensors}
        />
        {expressionError && (
          <SmallText type='danger'>{expressionError}</SmallText>
        )}
        <CantileverRow
          tPadding='0.5rem'
          leadingContent={
            editingDerivedId ? (
              <CantileverButton
                onClick={() => {
                  setNewDerived(NEW_DERIVED_VALUE_ENTRY)
                  setExpressionError(null)
                  setEditingDerivedId(null)
                }}>
                Cancel
              </CantileverButton>
            ) : null
          }
          trailingContent={
            <CantileverButton
              primary
              onClick={handleAddDerivedValue}
              disabled={
                !hasSelectedSensors ||
                !newDerived.data.name ||
                !newDerived.data.expression
              }>
              {editingDerivedId ? 'Update' : 'Create'}
            </CantileverButton>
          }
        />
      </SpacedSection>
    </SectionContent>
  )
}
